import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TopNavigation } from "@/components/top-navigation";
import { ErrorBoundary } from "@/components/error-boundary";
import { FavoritesProvider } from "@/hooks/use-favorites";
import { useAnalytics } from "@/hooks/use-analytics";
import { usePageMetadata } from "@/hooks/use-page-metadata";
import { useGlobalShortcuts } from "@/hooks/use-global-shortcuts";
import { initGA } from "@/lib/analytics";
import { F1_GUIDES } from "@shared/f1-guides";
import { F1GuidePage } from "@/components/f1-discovery";
import NotFound from "@/pages/not-found";
import { lazy, Suspense, useEffect } from "react";

// Lazy-load all routes so they don't bloat the initial bundle
const Dashboard = lazy(() => import("@/pages/dashboard"));
const DriversPage = lazy(() => import("@/pages/drivers"));
const SessionsPage = lazy(() => import("@/pages/sessions"));
const AnalysisPage = lazy(() => import("@/pages/analysis"));
const CalendarPage = lazy(() => import("@/pages/calendar"));
const StandingsPage = lazy(() => import("@/pages/standings"));
const CircuitsPage = lazy(() => import("@/pages/circuits"));
const StatsPage = lazy(() => import("@/pages/stats"));

function Router() {
  useAnalytics();
  usePageMetadata();
  useGlobalShortcuts();
  const [location] = useLocation();

  return (
    // Re-mount the boundary on route changes so a page that crashed once gets
    // a clean slate when the user navigates away and back.
    <ErrorBoundary key={location} context="this page">
      <Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Loading...</div>}>
        <Switch>
          <Route path="/" component={Dashboard} />
          <Route path="/drivers" component={DriversPage} />
          <Route path="/sessions" component={SessionsPage} />
          <Route path="/analysis" component={AnalysisPage} />
          <Route path="/calendar" component={CalendarPage} />
          <Route path="/standings" component={StandingsPage} />
          <Route path="/stats" component={StatsPage} />
          <Route path="/circuits"><CircuitsPage /></Route>
          <Route path="/circuits/:slug">{params => <CircuitsPage slug={params.slug} />}</Route>
          <Route path="/guides"><F1GuidePage /></Route>
          <Route path="/guides/:slug">{params => F1_GUIDES.some(guide => guide.slug === params.slug) ? <F1GuidePage slug={params.slug} /> : <NotFound />}</Route>
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  useEffect(() => {
    const stored = localStorage.getItem("theme") as "light" | "dark" | null;
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const theme = stored || (prefersDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", theme === "dark");
    
    initGA();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <FavoritesProvider>
          <div className="app-shell">
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Skip to main content
            </a>
            <TopNavigation />
            <div className="flex flex-1 min-h-0 min-w-0">
                <main id="main-content" tabIndex={-1} className="flex-1 min-w-0 overflow-auto">
                  <Router />
                </main>
            </div>
          </div>
          <Toaster />
        </FavoritesProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
